import { Component, inject, OnDestroy, OnInit } from '@angular/core'
import { Observable, Subject, interval } from 'rxjs'
import { switchMap, takeUntil } from 'rxjs/operators'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { CommonModule } from '@angular/common'
import { Store } from '@ngrx/store'
import { NgSelectModule } from '@ng-select/ng-select'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { PaginationComponent } from '../../template/pagination/pagination.component'
import { CronLog, CronSchedulePayload, CronService } from '../../../../services/cron.service'
import { Business } from '../../../../store/business/business.state'
import { selectAllBusinesses } from '../../../../store/business/business.selectors'
import { loadBusinesses } from '../../../../store/business/business.actions'
import { ActivatedRoute, RouterModule } from '@angular/router'

export enum CronName {
  ATTENDANCE = 'attendance',
  WEEKLY_OFF = 'weekly_off',
  SUBSCRIPTION = 'subscription',
  LEAVE_ACCRUAL = 'leave_accrual',
  PROBATION_BALANCE = 'probation_balance',
  ATTENDANCE_POLICY_ASSIGN = 'attendance_policy_assign',
  RESIGNATION = 'resignation',
  // SEQUENTIAL = 'sequential',
  // ABSENT = 'absent'
  SUBSCRIPTION_DOWNGRADE_WARNING = 'subscription_downgrade_warning',
  SUBSCRIPTION_DOWNGRADE_ENFORCEMENT = 'subscription_downgrade_enforcement',
  SUBSCRIPTION_STALE_SYNC = 'subscription_stale_sync',
  SUBSCRIPTION_FINALIZE_EXPIRED = 'subscription_finalize_expired',
  SUBSCRIPTION_EXPIRE_TRIALS = 'subscription_expire_trials',
  SEQUENTIAL_DAILY = 'sequential_daily'
}

// interface CronSchedule {
//   id: string
//   cronName: string
//   frequency: string
//   runTime: string
//   businessId?: string
//   businessName?: string
//   enabled: boolean
//   lastRun?: string
//   nextRun?: string
// }
export type CronScheduleType = 'once' | 'daily' | 'weekly' | 'monthly' | 'annually' | 'custom'

interface CronSchedule {
  id: string
  cronName: string

  scheduleType: CronScheduleType

  cronExpression?: string | null
  runAt?: string | null

  runTime?: string

  businessId?: string
  businessName?: string

  isActive: boolean

  lastRun?: string
  nextRun?: string

  timezone?: string
}

interface CronScheduleRule {
  allowFutureDate: boolean
  allowOnce: boolean
  allowRecurring: boolean
}

@Component({
  selector: 'app-cron-detail',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    CapitalizePipe,
    PaginationComponent,
    NgSelectModule
  ],
  templateUrl: './cron-detail.component.html',
  styleUrls: ['./cron-detail.component.scss']
})
export class CronDetailComponent implements OnInit, OnDestroy {
  private store = inject(Store) // Inject store using `inject()`

  logs: CronLog[] = []
  totalCount = 0
  currentPage = 1
  pageLimit = 10
  loading = false
  pageLastRecord = this.pageLimit

  availableCrons: string[] = []

  // filters
  filterCronName = ''
  filterStatus = ''
  filterBusinessId = ''
  filterFromDate = ''
  filterToDate = ''

  // run-modal state
  showRunModal = false
  runMode: 'single' | 'range' = 'single'
  runCronName = ''
  runDate = ''
  runStartDate = ''
  runEndDate = ''
  runBusinessId = ''
  runError = ''
  runSubmitting = false

  // details drawer
  selectedLog: CronLog | null = null
  selectedLogDetails: any[] = []

  private destroy$ = new Subject<void>()
  // cron-names.enum.ts
  activeTab: 'jobs' | 'schedules' = 'jobs'

  readonly statusOptions = ['queued', 'running', 'success', 'failed', 'stopped', 'paused']
  readonly cronOptions = [
    { label: 'Attendance', value: CronName.ATTENDANCE },
    { label: 'Weekly Off', value: CronName.WEEKLY_OFF },
    { label: 'Subscription', value: CronName.SUBSCRIPTION },
    { label: 'Leave Accrual', value: CronName.LEAVE_ACCRUAL },
    { label: 'Probation End', value: CronName.PROBATION_BALANCE },
    { label: 'Attendance Policy Assignment', value: CronName.ATTENDANCE_POLICY_ASSIGN },
    { label: 'Resignation', value: CronName.RESIGNATION },
    { label: 'Subscription Downgrade Warning', value: CronName.SUBSCRIPTION_DOWNGRADE_WARNING },
    {
      label: 'Subscription Downgrade Enforcement',
      value: CronName.SUBSCRIPTION_DOWNGRADE_ENFORCEMENT
    },
    { label: 'Subscription Stale Sync', value: CronName.SUBSCRIPTION_STALE_SYNC },
    { label: 'Subscription Finalize Expired', value: CronName.SUBSCRIPTION_FINALIZE_EXPIRED },
    { label: 'Subscription Expire Trials', value: CronName.SUBSCRIPTION_EXPIRE_TRIALS },
    { label: 'Sequential Daily', value: CronName.SEQUENTIAL_DAILY }
  ]
  showScheduleModal = false
  editingSchedule = false
  scheduleSubmitting = false
  scheduleError = ''
  scheduleLoading = false
  cronSchedules: CronSchedule[] = []

  scheduleForm: CronSchedule = {
    id: '',
    cronName: '',
    scheduleType: 'daily',
    runTime: '00:00',
    runAt: '',
    businessId: '',
    businessName: '',
    isActive: true
  }
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)
  weekDays = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' }
  ]

  selectedWeekDay = 1
  monthDays = Array.from({ length: 31 }, (_, i) => i + 1)

  selectedMonthDay = 1
  months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ]

  selectedMonth = 1
  selectedAnnualDay = 1
  timezones = [
    'UTC',
    'Asia/Kolkata',
    'Asia/Dubai',
    'Asia/Singapore',
    'Europe/London',
    'America/New_York',
    'America/Los_Angeles'
  ]

  readonly futureDateAllowed = new Set<string>([CronName.WEEKLY_OFF])
  selectedSchedule: CronSchedule | null = null
  scheduleHistoryLogs: any[] = []
  scheduleHistoryLoading = false
  scheduleHistoryTotal = 0
  scheduleHistoryPage = 1
  scheduleHistoryLimit = 20
  scheduleHistoryStatusCounts: Record<string, number> | null = null

  id = ''
  constructor(
    private cronService: CronService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id')!
    this.loadScheduleHistory()
    this.cronService.getAvailableCrons().subscribe(list => (this.availableCrons = list))
    // this.loadLogs()

    // ✅ Poll every 4s — sirf tab refresh karo jab koi running/queued row ho,
    // taaki khaali-khaali API load na ho jab sab settled hain
    // interval(4000)
    //   .pipe(
    //     takeUntil(this.destroy$),
    //     switchMap(() => {
    //       const hasActive = this.logs.some(l => l.status === 'running' || l.status === 'queued')
    //       return hasActive ? this.cronService.getLogs(this.buildQuery()) : []
    //     })
    //   )
    //   .subscribe((res: any) => {
    //     if (res && res.data) {
    //       this.logs = res.data
    //       this.totalCount = res.total
    //     }
    //   })

    // Poll every 4 seconds
    interval(4000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          const hasActive = this.logs.some(l => l.status === 'running' || l.status === 'queued')

          return hasActive
            ? this.cronService.getScheduleExecutionHistory(this.id, this.buildQuery())
            : []
        })
      )
      .subscribe((res: any) => {
        if (res?.data) {
          this.logs = res.data
          this.totalCount = res.total
          this.scheduleHistoryStatusCounts = res.statusCounts
        }
      })
    this.loadAllBusinesses()
    // this.loadCronSchedules()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  //   private buildQuery() {
  //     return {
  //       cronName: this.filterCronName || undefined,
  //       status: this.filterStatus || undefined,
  //       businessId: this.filterBusinessId || undefined,
  //       fromDate: this.filterFromDate || undefined,
  //       toDate: this.filterToDate || undefined,
  //       page: this.currentPage,
  //       limit: this.pageLimit
  //     }
  //   }

  private buildQuery() {
    const query: any = {
      page: this.currentPage,
      limit: this.pageLimit
    }

    if (this.filterCronName?.trim()) {
      query.cronName = this.filterCronName.trim()
    }

    if (this.filterStatus) {
      query.status = this.filterStatus
    }

    if (this.filterBusinessId) {
      query.businessId = this.filterBusinessId
    }

    if (this.filterFromDate) {
      query.fromDate = this.filterFromDate
    }

    if (this.filterToDate) {
      query.toDate = this.filterToDate
    }

    return query
  }

  loadLogs(): void {
    this.loading = true
    this.cronService.getLogs(this.buildQuery()).subscribe({
      next: res => {
        this.logs = res.data
        this.totalCount = res.total
        this.loading = false
      },
      error: () => (this.loading = false)
    })
  }

  onFilterChange(): void {
    this.currentPage = 1
    // this.loadLogs()
    this.loadScheduleHistory()
  }

  changePage(delta: number): void {
    const maxPage = Math.ceil(this.totalCount / this.pageLimit)
    const next = this.currentPage + delta
    if (next >= 1 && next <= maxPage) {
      this.currentPage = next
      //   this.loadLogs()
      this.loadScheduleHistory()
    }
  }

  // ---------------- Run modal ----------------

  openRunModal(): void {
    this.showRunModal = true
    this.runMode = 'single'
    this.runCronName = this.availableCrons[0] ?? ''
    this.runDate = ''
    this.runStartDate = ''
    this.runEndDate = ''
    this.runBusinessId = ''
    this.runError = ''
  }

  closeRunModal(): void {
    this.showRunModal = false
  }

  //   submitRun(): void {
  //     if (!this.runCronName) {
  //       this.runError = 'Select a cron type'
  //       return
  //     }
  //     // Single date
  //     if (this.runMode === 'single') {
  //       if (this.runDate && this.isFutureDate(this.runDate)) {
  //         if (!this.futureDateAllowed.has(this.runCronName)) {
  //           this.runError = 'Future date is not allowed for this cron'
  //           return
  //         }
  //       }
  //     }

  //     // Date range
  //     if (this.runMode === 'range') {
  //       if (!this.runStartDate || !this.runEndDate) {
  //         this.runError = 'Please select start date and end date'
  //         return
  //       }

  //       if (this.runStartDate > this.runEndDate) {
  //         this.runError = 'Start date cannot be greater than end date'
  //         return
  //       }

  //       // Future range check
  //       if (
  //         !this.futureDateAllowed.has(this.runCronName) &&
  //         (this.isFutureDate(this.runStartDate) || this.isFutureDate(this.runEndDate))
  //       ) {
  //         this.runError = 'Future dates are not allowed for this cron'
  //         return
  //       }
  //     }

  //     this.runError = ''
  //     this.runSubmitting = true

  //     const payload =
  //       this.runMode === 'range'
  //         ? {
  //             startDate: this.runStartDate,
  //             endDate: this.runEndDate,
  //             businessId: this.runBusinessId || undefined
  //           }
  //         : {
  //             date: this.runDate || undefined,
  //             businessId: this.runBusinessId || undefined
  //           }

  //     this.cronService.runCron(this.runCronName, payload).subscribe({
  //       next: () => {
  //         this.runSubmitting = false
  //         this.showRunModal = false
  //         this.loadLogs()
  //       },
  //       error: err => {
  //         this.runSubmitting = false
  //         this.runError = err?.error?.message || err?.message || 'Failed to start cron'
  //       }
  //     })
  //   }

  // ---------------- Row actions ----------------

  stop(log: CronLog): void {
    this.cronService.stopCron(log.id).subscribe(() =>
      // this.loadLogs()
      this.loadScheduleHistory()
    )
  }

  pause(log: CronLog): void {
    this.cronService.pauseCron(log.id).subscribe(() =>
      // this.loadLogs()
      this.loadScheduleHistory()
    )
  }

  restart(log: CronLog): void {
    this.cronService.restartCron(log.id).subscribe(() =>
      // this.loadLogs()
      this.loadScheduleHistory()
    )
  }

  viewDetails(log: CronLog): void {
    this.selectedLog = log
    this.cronService.getLogDetails(log.id, { limit: 100 }).subscribe(res => {
      this.selectedLogDetails = res.data
    })
  }

  closeDetails(): void {
    this.selectedLog = null
    this.selectedLogDetails = []
  }

  // ---------------- UI helpers ----------------

  statusClass(status: string): string {
    return `status-badge status-${status}`
  }

  canStop(status: string): boolean {
    return status === 'running'
  }
  canPause(status: string): boolean {
    return status === 'running'
  }
  canRestart(status: string): boolean {
    return ['failed', 'stopped', 'paused'].includes(status)
  }

  getCronLabel(value: string): string {
    return this.cronOptions.find(cron => cron.value === value)?.label || value
  }

  onPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentPage = newPage
      //   this.loadLogs() // Reload businesses for the new page
      this.loadScheduleHistory()
      this.updatePageLastRecord()
    }
  }

  private updatePageLastRecord() {
    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
  }

  onPageLimitChange() {
    this.currentPage = 1 // Reset to first page when limit changes
    this.updatePageLastRecord()
    // this.loadLogs()
    this.loadScheduleHistory()
  }

  openScheduleModal(): void {
    this.editingSchedule = false
    this.scheduleError = ''

    this.scheduleForm = {
      id: '',
      cronName: this.availableCrons[0] || '',
      scheduleType: 'daily',
      cronExpression: null,
      runAt: null,
      runTime: '00:00',
      businessId: '',
      businessName: '',
      isActive: true,
      timezone: 'UTC'
    }

    this.showScheduleModal = true
  }

  editSchedule(schedule: CronSchedule): void {
    this.editingSchedule = true
    this.scheduleError = ''

    this.scheduleForm = {
      ...schedule
    }

    this.showScheduleModal = true
  }

  closeScheduleModal(): void {
    this.showScheduleModal = false
    this.scheduleError = ''
  }

  toggleSchedule(schedule: CronSchedule): void {
    const newStatus = !schedule.isActive

    // API call
    // this.cronService.toggleSchedule(schedule.id, newStatus)

    schedule.isActive = newStatus
  }

  loadAllBusinesses() {
    const params: any = {}

    params.disabled = false

    this.store.dispatch(
      loadBusinesses({
        params: new URLSearchParams(params).toString()
      })
    )
  }

  customSearchFn = (term: string, item: any) => {
    const fullName = `${item.businessName}`.toLowerCase()
    return fullName.includes(term.toLowerCase())
  }

  private isFutureDate(date: string): boolean {
    if (!date) return false

    const selected = new Date(`${date}T00:00:00`)
    const today = new Date()

    today.setHours(0, 0, 0, 0)

    return selected > today
  }

  private readonly cronScheduleRules: Record<string, CronScheduleRule> = {
    [CronName.ATTENDANCE]: {
      allowFutureDate: false,
      allowOnce: false,
      allowRecurring: true
    },

    [CronName.WEEKLY_OFF]: {
      allowFutureDate: false,
      allowOnce: false,
      allowRecurring: true
    },

    [CronName.LEAVE_ACCRUAL]: {
      allowFutureDate: false,
      allowOnce: false,
      allowRecurring: true
    },

    [CronName.RESIGNATION]: {
      allowFutureDate: false,
      allowOnce: false,
      allowRecurring: true
    },

    // Example
    [CronName.SUBSCRIPTION]: {
      allowFutureDate: true,
      allowOnce: true,
      allowRecurring: true
    }
  }

  getCronScheduleRule(cronName: string): CronScheduleRule {
    return (
      this.cronScheduleRules[cronName] ?? {
        allowFutureDate: true,
        allowOnce: true,
        allowRecurring: true
      }
    )
  }

  viewScheduleHistory(schedule: CronSchedule): void {
    this.selectedSchedule = schedule
    this.scheduleHistoryPage = 1
    this.loadScheduleHistory()
  }

  loadScheduleHistory(): void {
    if (!this.id) return
    this.scheduleHistoryLoading = true
    this.loading = true
    this.cronService.getScheduleExecutionHistory(this.id, this.buildQuery()).subscribe({
      next: (res: any) => {
        //   this.scheduleHistoryLogs = res.data
        //   this.scheduleHistoryTotal = res.total
        this.scheduleHistoryStatusCounts = res.statusCounts
        this.scheduleHistoryLoading = false
        this.logs = res.data
        this.totalCount = res.total
        this.loading = false
      },
      error: () => {
        this.scheduleHistoryLoading = false
      }
    })
  }

  onScheduleHistoryPageChange(page: number): void {
    this.scheduleHistoryPage = page
    this.loadScheduleHistory()
  }

  closeScheduleHistory(): void {
    this.selectedSchedule = null
    this.scheduleHistoryLogs = []
    this.scheduleHistoryStatusCounts = null
  }
}
